/**
 * Composer types
 *
 * Composer is the seeded `composer` collaboration (innovate-service). The guide
 * agent returns a strict envelope each turn; on the build turn the synchronous
 * builder agent's result rides back in the same response under `buildResult`
 * (the backend build is synchronous — there is no realtime stream).
 *
 * Rung 5 (conversational) adds the `conversational: true` flag to the spec and
 * `conversational: true` to the build result, signalling that the built endpoint
 * supports multi-turn conversation via `collaborationId` continuation.
 */

export interface ComposerOption {
  label: string;
  value: string;
}

/**
 * The option value that declines rather than answers (task 1137): the
 * web-page offer's "Not now" (seeded by task 1136). A chip carrying it sends
 * no turn; the sidebar only takes the chips away.
 */
export const DISMISS_OPTION_VALUE = '__dismiss__';

/** One stage of a multi-step capability (rung 2). */
export interface ComposerStep {
  name?: string;
  intent?: string;
  prompt?: { name?: string; content?: string };
  input?: Record<string, unknown> | null;
  output?: Record<string, unknown> | null;
}

/** One route of a branching capability (rung 3). */
export interface ComposerBranch {
  name?: string;
  /** Human-readable label for this route, e.g. "Refund", "Complaint", "Other". */
  label?: string;
  /** JavaScript condition expression; null = explicit default catch-all. */
  condition?: string | null;
  intent?: string;
  prompt?: { name?: string; content?: string };
  input?: Record<string, unknown> | null;
  output?: Record<string, unknown> | null;
}

/**
 * External actions for a tool-using capability (rung 4). Present in any spec
 * shape (single, multi-step, branching) when the capability reaches out to
 * external systems to look things up or take actions.
 */
export interface ComposerExternalActions {
  /** Plain-language summary of what the capability looks up or does externally. */
  description?: string;
  /** Built-in registry tool names (e.g. "http_fetch", "file_read"). */
  registryTools?: string[];
  /** Named MCP connections (e.g. "create-api-mcp", "innovate-messaging-mcp"). */
  mcpConnections?: string[];
}

/**
 * The assistant identity for a conversational capability (rung 5). Describes the
 * assistant's purpose, voice, and knowledge for the guide — the builder turns this
 * into a system prompt that gives the assistant its personality.
 */
export interface ComposerAssistant {
  name?: string;
  purpose?: string;
  whatItKnows?: string;
}

export interface ComposerSpec {
  intent?: string;
  llmConfigName?: string;
  /**
   * True for rung 5 (conversational): the capability is a multi-turn assistant,
   * not a one-shot action. The builder emits the simple-chat shape (entry node +
   * agent node + loop-back edge, responseQueued: false) instead of a one-shot graph.
   */
  conversational?: boolean;
  /**
   * The assistant's identity for a conversational capability (rung 5). Present
   * instead of `agent`/`prompt` when `conversational` is true.
   */
  assistant?: ComposerAssistant;
  prompt?: { name?: string; content?: string };
  agent?: {
    name?: string;
    input?: Record<string, unknown> | null;
    output?: Record<string, unknown> | null;
  };
  /**
   * Ordered stages for a multi-step capability (rung 2). Present instead of
   * `agent`/`prompt` when the capability runs as a sequence.
   */
  steps?: ComposerStep[];
  /**
   * Router metadata for a branching capability (rung 3). Present alongside
   * `branches` when the capability classifies input and handles each kind differently.
   */
  router?: {
    intent?: string;
    routingField?: string;
  };
  /** Ordered routes for a branching capability (rung 3). */
  branches?: ComposerBranch[];
  /** The converging output step for a branching capability (rung 3). */
  converge?: {
    name?: string;
    intent?: string;
    prompt?: { name?: string; content?: string };
    input?: Record<string, unknown> | null;
    output?: Record<string, unknown> | null;
  };
  /**
   * External data sources and actions for a tool-using capability (rung 4).
   * Present in any spec shape when the capability reaches external systems.
   */
  externalActions?: ComposerExternalActions;
  /**
   * The capability's entry point. `name` and `description` are MODIFY-mode only
   * (task 808): they are how a change conversation says "rewrite the Name/the
   * Description a reader sees on this service's page", and they are the only
   * fields that do it. `intent` is the guide's own summary of behavior and is
   * NOT the description — a modify spec that carried the requested text in
   * `intent` (or in `assistant.purpose`) reported success and changed nothing.
   *
   * A field that is absent means "leave it alone", so the confirmation card
   * previews only what is actually present and actually different.
   */
  endpoint?: { path?: string; method?: string; name?: string; description?: string };
  /** `"modify"` when this conversation changes an existing service (task 753). */
  mode?: string;
  /** The service being changed, in modify mode. */
  targetEndpointId?: string;
  [key: string]: unknown;
}

export interface ComposerResourceIds {
  promptId?: string;
  agentId?: string;
  configId?: string;
  endpointId?: string;
  /** Multi-step builds return one entry per stage, in order. */
  agentIds?: string[];
  promptIds?: string[];
  /** Branching builds return the router agent/prompt. */
  routerAgentId?: string;
  routerPromptId?: string;
  /** Branching builds return one entry per branch, in order. */
  branchAgentIds?: string[];
  branchPromptIds?: string[];
  /** Branching builds return the converge agent/prompt. */
  convergeAgentId?: string;
  convergePromptId?: string;
  /** Tool-using builds (rung 4) return the IDs of linked MCP connections. */
  mcpConnectionIds?: string[];
}

export interface ComposerBuildResult {
  status: 'succeeded' | 'failed';
  /**
   * True when the build produced a multi-turn conversational assistant (rung 5).
   * The "Try it" action should open a conversational chat runner, not the one-shot
   * request form used for rungs 1–4.
   */
  conversational?: boolean;
  resourceIds?: ComposerResourceIds;
  endpointPath?: string;
  /**
   * Full slug-qualified invocation URL of the endpoint the builder created
   * (task 385), e.g. `https://api.example.com/api/acme/summarize`. Composed
   * server-side — display it verbatim rather than rebuilding it from
   * `endpointPath`, which carries no account slug. Absent on older responses,
   * where the app falls back to the unqualified `/api/{endpointPath}` shape.
   */
  endpointUrl?: string;
  error?: string;
  /**
   * The web-page offer sentence (task 1136), on a succeeded create build of a
   * service people use directly, e.g. "Since your support team chats with it
   * directly, it could use a web page. Want one?". Its two chips arrive as the
   * builder output's `options`, which the turn normalizer lifts onto the reply
   * (task 1137). Absent on every other build.
   */
  message?: string;
}

/**
 * A structured clarifying question from the guide, rendered as a form field.
 * The User's answers are serialized back into one plain chat message
 * (`Label: answer` per line) — there is no separate answer transport.
 */
export interface ComposerQuestion {
  id: string;
  label: string;
  type: 'text' | 'choice' | 'boolean';
  /** Choice type only: the selectable answers. */
  options?: ComposerOption[];
  /** Text type only: example answer shown as the placeholder. */
  placeholder?: string;
  required?: boolean;
}

/** The guide's per-turn output envelope (the collaboration's mapped output). */
export interface ComposerEnvelope {
  phase: 'asking' | 'building';
  message: string;
  options?: ComposerOption[];
  questions?: ComposerQuestion[];
  spec?: ComposerSpec;
  buildResult?: ComposerBuildResult;
}

/** Shape of `data` returned by POST /api/composer. */
export interface ComposerResponse {
  requestId: string;
  collaborationId: string;
  status: string;
  output: ComposerEnvelope | null;
}

/** A rendered chat message in the Composer UI. */
export interface ComposerChatMessage {
  id: string;
  role: 'human' | 'assistant';
  text: string;
  options?: ComposerOption[];
  questions?: ComposerQuestion[];
  buildResult?: ComposerBuildResult;
}
